import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Layers,
  LogIn,
  LogOut,
  Lock,
  PlayCircle,
  ScanLine,
  ShieldAlert,
  Users2,
} from "lucide-react";
import {
  attendanceLabel,
  downloadCsv,
  lateByMinutes,
  toCsv,
} from "@/lib/csv";
import { formatUserAudit, formatClosedBy } from "@/lib/audit";
import { api, formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import {
  Field,
  OutlineButton,
  PrimaryButton,
  inputCls,
  selectCls,
} from "@/components/masters/UI";
import { useAuth } from "@/context/AuthContext";

const todayStr = () => {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const AttendancePage = () => {
  const { user, isAdmin, isLocationAdmin, isSupervisor, isAsstSupervisor, selectedLocationId } = useAuth();
  const navigate = useNavigate();
  const { sid: routeSid } = useParams();

  const isAllLocations = isAdmin && (!selectedLocationId || selectedLocationId === "all");

  const [shifts, setShifts] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [shiftId, setShiftId] = useState("");
  const [session, setSession] = useState(null);
  const [records, setRecords] = useState([]);
  const [scanInput, setScanInput] = useState("");
  const [mode, setMode] = useState("in"); // "in" | "out"
  const [confirm, setConfirm] = useState(null); // { record, mode }
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);

  const scanRef = useRef(null);
  const confirmTimer = useRef(null);

  // ---------- lookups + auto-resume today's session --------------------

  const loadShifts = useCallback(async () => {
    if (isAllLocations) {
      setShifts([]);
      return [];
    }
    try {
      const params = {};
      if (isAdmin && selectedLocationId && selectedLocationId !== "all") {
        params.location_id = selectedLocationId;
      }
      const { data } = await api.get("/attendance/shifts", { params });
      setShifts(data);
      if (data.length && !shiftId) setShiftId(data[0].id);
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      return [];
    }
  }, [shiftId, isAdmin, selectedLocationId, isAllLocations]);

  const resumeIfExists = useCallback(async () => {
    try {
      if (routeSid) {
        const s = await api.get(`/attendance/sessions/${routeSid}`);
        setSession(s.data);
        setShiftId(s.data.shift_id);
        setDate(s.data.date);
        const recs = await api.get(`/attendance/sessions/${routeSid}/records`);
        setRecords(recs.data);
        return;
      }
      if (isAllLocations) {
        setSession(null);
        setRecords([]);
        return;
      }
      const params = { date: todayStr() };
      if (isAdmin && selectedLocationId && selectedLocationId !== "all") {
        params.location_id = selectedLocationId;
      }
      const { data } = await api.get("/attendance/sessions", { params });
      const open = data.find((s) => s.status === "open");
      const target = open || data[0];
      if (target) {
        setSession(target);
        setShiftId(target.shift_id);
        setDate(target.date);
        const recs = await api.get(`/attendance/sessions/${target.id}/records`);
        setRecords(recs.data);
      } else {
        setSession(null);
        setRecords([]);
      }
    } catch (e) {
      // Sid from URL was invalid or not in this Location's scope — bounce back to list.
      if (routeSid) {
        toast.error(formatApiError(e));
        navigate("/sessions");
      }
    }
  }, [routeSid, navigate, isAdmin, selectedLocationId, isAllLocations]);

  useEffect(() => {
    (async () => {
      await loadShifts();
      await resumeIfExists();
    })();
  }, [loadShifts, resumeIfExists]);

  // When Scan-In is closed, snap mode to "out" so the operator can only scan out.
  useEffect(() => {
    if (session && session.status !== "open" && mode !== "out") setMode("out");
  }, [session, mode]);

  // Focus the scan input whenever an active session is present.
  useEffect(() => {
    if (session) scanRef.current?.focus();
  }, [session?.id, session?.status, mode]);

  useEffect(() => () => clearTimeout(confirmTimer.current), []);

  // ---------- actions --------------------------------------------------

  const start = async (e) => {
    e?.preventDefault();
    if (isAllLocations) {
      setError("Select a Location first.");
      return;
    }
    if (!shiftId || !date) {
      setError("Pick a date and a shift first.");
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const payload = {
        date,
        shift_id: shiftId,
        ...(isAdmin && selectedLocationId && selectedLocationId !== "all"
          ? { location_id: selectedLocationId }
          : {}),
      };
      const { data } = await api.post("/attendance/sessions/start", payload);
      setSession(data);
      setMode(data.status === "open" ? "in" : "out");
      const recs = await api.get(`/attendance/sessions/${data.id}/records`);
      setRecords(recs.data);
      toast.success("Attendance session started");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setStarting(false);
    }
  };

  const submitScan = async (e) => {
    e?.preventDefault();
    if (isAllLocations) {
      setError("Select a Location first.");
      return;
    }
    if (!session || busy) return;
    const val = scanInput.trim();
    if (!val) return;
    setBusy(true);
    setError(null);
    try {
      const path = mode === "out" ? "scan-out" : "scan-in";
      const payload = {
        employee_id: val,
        ...(isAdmin && selectedLocationId && selectedLocationId !== "all"
          ? { location_id: selectedLocationId }
          : {}),
      };
      const { data } = await api.post(
        `/attendance/sessions/${session.id}/${path}`,
        payload
      );
      setSession((s) => ({ ...s, ...data.session }));
      setRecords((r) =>
        mode === "out"
          ? r.map((x) => (x.id === data.record.id ? data.record : x))
          : [data.record, ...r]
      );
      setConfirm({ record: data.record, mode });
      setScanInput("");
      clearTimeout(confirmTimer.current);
      confirmTimer.current = setTimeout(() => setConfirm(null), 3200);
      scanRef.current?.focus();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const closeScanIn = async () => {
    if (!session) return;
    if (!window.confirm("Close Scan-In for this session? This can't be undone.")) return;
    setClosing(true);
    try {
      const { data } = await api.post(`/attendance/sessions/${session.id}/close`);
      setSession(data);
      toast.success("Session Scan-In closed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClosing(false);
    }
  };

  const changeSession = () => {
    if (routeSid) {
      // Came in from Sessions history — go back to the list.
      navigate("/sessions");
      return;
    }
    setSession(null);
    setRecords([]);
    setConfirm(null);
    setError(null);
    setMode("in");
  };

  const exportAttendance = async () => {
    if (!session) return;
    try {
      const rosterRes = await api.get(`/attendance/sessions/${session.id}/roster`);
      const roster = rosterRes.data || [];
      const recordByEmp = {};
      for (const r of records) recordByEmp[r.employee_id] = r;
      // Merge: every roster row → record if scanned, else "absent".
      const merged = roster.map((e) => {
        const rec = recordByEmp[e.employee_id];
        if (rec) return { ...rec, __rosterName: e.name, __rosterDept: e.department_name };
        return {
          employee_id: e.employee_id,
          name: e.name,
          department_name: e.department_name,
          shift_name: session.shift_name,
          status: "absent",
          scan_in_time: "",
          out_time: "",
        };
      });
      // Any scanned employee NOT in roster (edge case: employee re-assigned) — still include.
      for (const r of records) {
        if (!roster.some((e) => e.employee_id === r.employee_id)) merged.push(r);
      }
      const csv = toCsv(merged, [
        { header: "Employee ID", get: (r) => r.employee_id },
        { header: "Name", get: (r) => r.name },
        { header: "Department", get: (r) => r.department_name || "" },
        { header: "Shift", get: (r) => r.shift_name || session.shift_name },
        { header: "Status", get: (r) => attendanceLabel(r) },
        { header: "Is Late", get: (r) => (r.status === "absent" ? "" : (r.is_late ? "Yes" : "No")) },
        { header: "Scan-In", get: (r) => r.scan_in_time || "" },
        { header: "Scan-Out", get: (r) => r.out_time || "" },
        {
          header: "Late By",
          get: (r) => {
            const late = lateByMinutes(r, session);
            return late != null ? String(late) : "";
          },
        },
        {
          header: "Scanned By",
          get: (r) => {
            const inUser = formatUserAudit(r.scanned_in_by);
            const outUser = formatUserAudit(r.scanned_out_by);
            if (inUser && outUser && inUser !== outUser) return `In: ${inUser}, Out: ${outUser}`;
            return inUser || outUser || "";
          },
        },
        {
          header: "Closed By",
          get: () => formatUserAudit(session.closed_by) || "",
        },
      ]);
      downloadCsv(
        `attendance-${session.date}-${session.shift_name.replace(/\s+/g, "_")}`,
        csv
      );
      toast.success(`Exported ${merged.length} rows`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ---------- derived --------------------------------------------------

  const canScanIn =
    session?.status === "open" &&
    (isSupervisor || isAsstSupervisor || isAdmin || isLocationAdmin) &&
    !isAllLocations;
  // Assistant Supervisor: read-only once Scan-In is closed. Supervisor and
  // Administrator may always scan-out (past sessions included).
  const readOnly =
    !!session && isAsstSupervisor && session.status !== "open";
  const canScanOut = !!session && !readOnly && !isAllLocations;

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="attendance-page">
      <AppHeader />

      <PageShell
        eyebrow="Attendance"
        title={session ? `Session · ${session.date}` : "Today's session"}
        subtitle={
          session
            ? `${session.shift_name} · ${session.shift_start}–${session.shift_end} · grace ${session.grace_minutes}m`
            : isAdmin
            ? "Select a Location from Scope and pick a date and shift to start attendance."
            : "Pick a date and shift for your location and start recording scans."
        }
        primaryAction={
          session ? (
            <div className="flex items-center gap-2">
              <OutlineButton testId="att-change-session" onClick={changeSession}>
                Change session
              </OutlineButton>
              <OutlineButton testId="att-export" onClick={exportAttendance}>
                <Download className="mr-1 inline h-4 w-4" />
                Export
              </OutlineButton>
              {(isSupervisor || isAdmin || isLocationAdmin) && canScanIn && (
                <PrimaryButton
                  testId="att-close-session"
                  onClick={closeScanIn}
                  disabled={closing || isAllLocations}
                >
                  <Lock className="mr-1 inline h-4 w-4" />
                  {closing ? "Closing…" : "Close Scan-In"}
                </PrimaryButton>
              )}
              {(isSupervisor || isAdmin || isLocationAdmin) && !canScanIn && (
                <PrimaryButton
                  testId="att-review-allocation"
                  onClick={() => navigate(`/allocation/${session.id}`)}
                >
                  <Layers className="mr-1 inline h-4 w-4" />
                  Review Allocation
                </PrimaryButton>
              )}
            </div>
          ) : null
        }
        testId="attendance-shell"
      >
        {!session ? (
          <SessionPicker
            date={date}
            setDate={setDate}
            shifts={shifts}
            shiftId={shiftId}
            setShiftId={setShiftId}
            onStart={start}
            starting={starting}
            error={error}
            isAllLocations={isAllLocations}
            isAdmin={isAdmin}
          />
        ) : (
          <>
            <SessionSummary session={session} />

            {isAllLocations && (
              <div
                className="mt-4 flex items-center gap-3 rounded-2xl border p-4 text-sm font-semibold"
                data-testid="att-all-locations-warning"
                style={{
                  backgroundColor: "rgba(247, 144, 9, 0.10)",
                  borderColor: "rgba(247, 144, 9, 0.35)",
                  color: "#92400e",
                }}
              >
                <AlertCircle className="h-4 w-4" />
                Select a Location first
              </div>
            )}

            {readOnly && (
              <div
                className="mt-4 flex items-center gap-3 rounded-2xl border p-4 text-sm"
                data-testid="att-readonly-banner"
                style={{
                  backgroundColor: "rgba(0, 53, 158, 0.06)",
                  borderColor: "rgba(0, 53, 158, 0.25)",
                  color: "var(--vision-navy)",
                }}
              >
                <Eye className="h-4 w-4" />
                Read-only. Assistant Supervisors can view past sessions but cannot scan into them once Scan-In is closed.
              </div>
            )}

            {!canScanIn && !readOnly && !isAllLocations && (
              <div
                className="mt-4 flex items-center gap-3 rounded-2xl border p-4 text-sm"
                data-testid="att-closed-banner"
                style={{
                  backgroundColor: "rgba(52, 73, 94, 0.06)",
                  borderColor: "rgba(52, 73, 94, 0.2)",
                  color: "var(--vision-slate)",
                }}
              >
                <Lock className="h-4 w-4" />
                <span>
                  Scan-In is closed for this session. {formatClosedBy(session) ? `${formatClosedBy(session)}. ` : ""}Scan Out is still available below.
                </span>
              </div>
            )}

            {!readOnly && (
              <ScanCard
                inputRef={scanRef}
                value={scanInput}
                onChange={setScanInput}
                onSubmit={submitScan}
                busy={busy}
                error={error}
                confirm={confirm}
                mode={mode}
                setMode={setMode}
                canScanIn={canScanIn}
                canScanOut={canScanOut}
                isAllLocations={isAllLocations}
              />
            )}

            <RecentScans records={records} session={session} />
          </>
        )}
      </PageShell>
    </div>
  );
};

// -----------------------------------------------------------------------

const SessionPicker = ({ date, setDate, shifts, shiftId, setShiftId, onStart, starting, error, isAllLocations, isAdmin }) => {
  const noShifts = shifts.length === 0;
  return (
    <div
      className="rounded-2xl border bg-white p-6 shadow-card"
      style={{ borderColor: "rgba(52,73,94,0.08)" }}
      data-testid="att-picker"
    >
      <div className="mb-5 flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ backgroundColor: "rgba(247, 144, 9, 0.14)", color: "var(--vision-orange)" }}
        >
          <PlayCircle className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display text-lg font-bold text-vision-ink">Start attendance</div>
          <div className="text-xs text-vision-slate/60">
            {isAdmin ? "Location is selected via the Scope switcher." : "Location is picked up from your account."}
          </div>
        </div>
      </div>

      {isAllLocations ? (
        <div
          className="mb-4 rounded-2xl border p-4 text-sm font-semibold"
          data-testid="att-select-location-notice"
          style={{
            backgroundColor: "rgba(247, 144, 9, 0.10)",
            borderColor: "rgba(247, 144, 9, 0.35)",
            color: "#92400e",
          }}
        >
          Select a Location first
        </div>
      ) : noShifts && (
        <div
          className="mb-4 rounded-2xl border p-4 text-sm"
          data-testid="att-no-shifts"
          style={{
            backgroundColor: "rgba(242, 153, 74, 0.10)",
            borderColor: "rgba(242, 153, 74, 0.35)",
            color: "#7a3f0a",
          }}
        >
          No active Shifts for your Location yet. Ask an Administrator to add one under
          <b> Masters → Shifts</b>.
        </div>
      )}

      <form onSubmit={onStart} className="grid gap-4 md:grid-cols-[1fr_1.4fr_auto] md:items-end">
        <Field label="Date">
          <input
            type="date"
            className={inputCls}
            data-testid="att-date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label="Shift">
          <select
            className={selectCls}
            data-testid="att-shift"
            value={shiftId}
            onChange={(e) => setShiftId(e.target.value)}
            disabled={noShifts || isAllLocations}
          >
            <option value="">Select a shift…</option>
            {shifts.map((s) => {
              const label = `${s.name} · ${s.start_time}-${s.end_time} · grace ${s.grace_minutes}m`;
              return (
                <option key={s.id} value={s.id} label={label}>
                  {label}
                </option>
              );
            })}
          </select>
        </Field>
        <div className="mt-1">
          <PrimaryButton
            testId="att-start"
            disabled={starting || noShifts || isAllLocations}
            type="submit"
            title={isAllLocations ? "Select a Location first" : undefined}
          >
            {starting ? "Starting…" : (<><PlayCircle className="mr-1 inline h-4 w-4" /> Start Attendance</>)}
          </PrimaryButton>
        </div>
      </form>

      {error && (
        <div
          data-testid="att-picker-error"
          className="mt-4 rounded-xl border px-4 py-3 text-sm"
          style={{
            backgroundColor: "rgba(231, 76, 60, 0.08)",
            borderColor: "rgba(231, 76, 60, 0.3)",
            color: "var(--vision-error)",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------

const SessionSummary = ({ session }) => {
  const isOpen = session.status === "open";
  return (
    <div className="grid gap-4 md:grid-cols-5" data-testid="att-session-summary">
      <SummaryTile
        label="Status"
        testId="att-summary-status"
        value={
          <div>
            <span
              className="chip"
              style={
                isOpen
                  ? {
                      backgroundColor: "rgba(39, 174, 96, 0.14)",
                      color: "#1e8a4a",
                      border: "1px solid rgba(39, 174, 96, 0.35)",
                    }
                  : {
                      backgroundColor: "rgba(52, 73, 94, 0.08)",
                      color: "var(--vision-slate)",
                      border: "1px solid rgba(52, 73, 94, 0.20)",
                    }
              }
            >
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: isOpen ? "var(--vision-success)" : "var(--vision-slate)" }}
              />
              {isOpen ? "Open" : "Scan-In Closed"}
            </span>
            {formatClosedBy(session) && (
              <div
                className="mt-1.5 text-xs font-semibold text-vision-slate"
                data-testid="att-closed-by"
              >
                {formatClosedBy(session)}
              </div>
            )}
          </div>
        }
        icon={isOpen ? PlayCircle : Lock}
        tint="peach"
      />
      <SummaryTile
        label="Date"
        testId="att-summary-date"
        value={<span className="font-display text-2xl font-extrabold text-vision-ink">{session.date}</span>}
        icon={CalendarDays}
        tint="navy"
      />
      <SummaryTile
        label="Present"
        testId="att-summary-present"
        value={
          <span className="font-display text-2xl font-extrabold text-vision-ink numeric-tabular">
            {session.present_count ?? 0}
          </span>
        }
        icon={Users2}
        tint="orange"
      />
      <SummaryTile
        label="Late"
        testId="att-summary-late"
        value={
          <span className="font-display text-2xl font-extrabold text-vision-ink numeric-tabular">
            {session.late_count ?? 0}
          </span>
        }
        icon={AlertCircle}
        tint="peach"
      />
      <SummaryTile
        label="Scanned Out"
        testId="att-summary-out"
        value={
          <span className="font-display text-2xl font-extrabold text-vision-ink numeric-tabular">
            {session.scanned_out_count ?? 0}
          </span>
        }
        icon={LogOut}
        tint="navy"
      />
    </div>
  );
};

const tintStyles = {
  peach:  { bg: "var(--vision-peach)",             fg: "var(--vision-ink)"    },
  navy:   { bg: "rgba(0, 53, 158, 0.10)",          fg: "var(--vision-navy)"   },
  slate:  { bg: "rgba(52, 73, 94, 0.10)",          fg: "var(--vision-slate)"  },
  orange: { bg: "rgba(247, 144, 9, 0.14)",         fg: "var(--vision-orange)" },
};

const SummaryTile = ({ label, value, icon: Icon, tint, testId }) => (
  <div
    className="rounded-2xl border bg-white p-5 shadow-card"
    style={{ borderColor: "rgba(52,73,94,0.08)" }}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-xl"
        style={{ backgroundColor: tintStyles[tint].bg, color: tintStyles[tint].fg }}
      >
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60">
      {label}
    </div>
    <div className="mt-1">{value}</div>
  </div>
);

// -----------------------------------------------------------------------

const ModeToggle = ({ mode, setMode, canScanIn, canScanOut }) => (
  <div
    className="inline-flex rounded-full bg-[color:var(--vision-ice)] p-1"
    data-testid="att-mode-toggle"
  >
    <ModePill
      active={mode === "in"}
      disabled={!canScanIn}
      onClick={() => canScanIn && setMode("in")}
      testId="att-mode-in"
      icon={LogIn}
      label="Scan In"
      title={canScanIn ? "Record Scan-In" : "Scan-In is closed for this session"}
    />
    <ModePill
      active={mode === "out"}
      disabled={!canScanOut}
      onClick={() => canScanOut && setMode("out")}
      testId="att-mode-out"
      icon={LogOut}
      label="Scan Out"
    />
  </div>
);

const ModePill = ({ active, disabled, onClick, testId, icon: Icon, label, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    title={title}
    className="chip"
    style={
      active
        ? {
            backgroundColor: "var(--vision-navy)",
            color: "#fff",
            border: "1px solid var(--vision-navy)",
          }
        : disabled
        ? {
            backgroundColor: "transparent",
            color: "rgba(52,73,94,0.40)",
            border: "1px solid transparent",
            cursor: "not-allowed",
          }
        : {
            backgroundColor: "transparent",
            color: "var(--vision-slate)",
            border: "1px solid transparent",
          }
    }
  >
    <Icon className="h-3.5 w-3.5" />
    {label}
  </button>
);

const ScanCard = ({
  inputRef, value, onChange, onSubmit, busy, error, confirm,
  mode, setMode, canScanIn, canScanOut, isAllLocations,
}) => {
  const isOut = mode === "out";
  return (
    <div
      className="mt-4 rounded-2xl border bg-white p-6 shadow-card"
      style={{ borderColor: "rgba(52,73,94,0.08)" }}
      data-testid="att-scan-card"
    >
      {isAllLocations && (
        <div
          data-testid="att-scan-guard-warning"
          className="mb-4 rounded-xl border px-4 py-2 text-sm font-semibold text-center"
          style={{
            backgroundColor: "rgba(247, 144, 9, 0.10)",
            borderColor: "rgba(247, 144, 9, 0.35)",
            color: "#92400e",
          }}
        >
          Select a Location first
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl"
            style={{
              backgroundColor: isOut ? "rgba(0, 53, 158, 0.10)" : "rgba(247, 144, 9, 0.14)",
              color: isOut ? "var(--vision-navy)" : "var(--vision-orange)",
            }}
          >
            <ScanLine className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg font-bold text-vision-ink" data-testid="att-scan-heading">
              {isOut ? "Scan / Enter Employee ID · Out" : "Scan / Enter Employee ID · In"}
            </div>
            <div className="text-xs text-vision-slate/60">
              {isOut
                ? "Records leaving time on an existing attendance record."
                : "Records arrival against the shift's grace window."}
            </div>
          </div>
        </div>

        <ModeToggle
          mode={mode}
          setMode={setMode}
          canScanIn={canScanIn}
          canScanOut={canScanOut}
        />
      </div>

      <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
        <input
          ref={inputRef}
          className={inputCls + " text-lg font-display font-semibold"}
          data-testid="att-scan-input"
          placeholder={isAllLocations ? "Select a Location first" : isOut ? "e.g. VSN-1041 (already scanned in)" : "e.g. VSN-1041"}
          disabled={isAllLocations}
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => (e.key === "Enter" ? onSubmit(e) : null)}
        />
        <PrimaryButton
          testId={isOut ? "att-scan-submit-out" : "att-scan-submit"}
          disabled={busy || !value.trim() || isAllLocations}
          title={isAllLocations ? "Select a Location first" : undefined}
        >
          {busy ? "Recording…" : isOut ? "Record scan-out" : "Record scan"}
        </PrimaryButton>
      </form>

      {error && (
        <div
          data-testid="att-scan-error"
          className="mt-4 rounded-xl border px-4 py-3 text-sm"
          style={{
            backgroundColor: "rgba(231, 76, 60, 0.08)",
            borderColor: "rgba(231, 76, 60, 0.3)",
            color: "var(--vision-error)",
          }}
        >
          {error}
        </div>
      )}

      {confirm && <ScanConfirm record={confirm.record} mode={confirm.mode} />}
    </div>
  );
};

// -----------------------------------------------------------------------

const ScanConfirm = ({ record, mode }) => {
  if (mode === "out") {
    return (
      <div
        className="mt-4 flex items-center gap-4 rounded-2xl border p-4"
        data-testid="att-scan-confirm"
        style={{
          borderColor: "rgba(0, 53, 158, 0.35)",
          backgroundColor: "rgba(0, 53, 158, 0.06)",
        }}
      >
        <div
          className="flex h-11 w-11 items-center justify-center rounded-full text-white"
          style={{ backgroundColor: "var(--vision-navy)" }}
        >
          <LogOut className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-display text-base font-bold text-vision-ink">{record.name}</span>
            <span className="text-xs text-vision-slate/60">· {record.employee_id}</span>
          </div>
          <div className="mt-0.5 text-sm text-vision-slate">
            {record.department_name} · {record.shift_name} · In {record.scan_in_time}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-base font-bold text-vision-ink numeric-tabular">
            {record.out_time}
          </div>
          <div
            className="mt-0.5 text-xs font-semibold uppercase tracking-[0.1em]"
            style={{ color: "var(--vision-navy)" }}
            data-testid="att-scan-status"
          >
            Scanned Out
          </div>
        </div>
      </div>
    );
  }
  const present = record.status === "present";
  const label = attendanceLabel(record);
  return (
    <div
      className="mt-4 flex items-center gap-4 rounded-2xl border p-4"
      data-testid="att-scan-confirm"
      style={{
        borderColor: present ? "rgba(39, 174, 96, 0.35)" : "rgba(242, 153, 74, 0.45)",
        backgroundColor: present ? "rgba(39, 174, 96, 0.08)" : "rgba(242, 153, 74, 0.10)",
      }}
    >
      <div
        className="flex h-11 w-11 items-center justify-center rounded-full text-white"
        style={{ backgroundColor: present ? "var(--vision-success)" : "var(--vision-warning)" }}
      >
        {present ? <CheckCircle2 className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-display text-base font-bold text-vision-ink">{record.name}</span>
          <span className="text-xs text-vision-slate/60">· {record.employee_id}</span>
        </div>
        <div className="mt-0.5 text-sm text-vision-slate">
          {record.department_name} · {record.shift_name}
        </div>
      </div>
      <div className="text-right">
        <div className="font-display text-base font-bold text-vision-ink numeric-tabular">
          {record.scan_in_time}
        </div>
        <div
          className="mt-0.5 text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1 justify-end"
          style={{ color: "#1e8a4a" }}
          data-testid="att-scan-status"
        >
          <span>{label}</span>
          {record.is_late && (
            <span
              className="chip text-[10px] lowercase tracking-normal font-semibold"
              style={{
                backgroundColor: "rgba(235, 87, 87, 0.12)",
                color: "var(--vision-error)",
                border: "1px solid rgba(235, 87, 87, 0.35)",
              }}
            >
              Late
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------

const RecentScans = ({ records, session }) => (
  <div
    className="mt-6 rounded-2xl border bg-white shadow-card"
    style={{ borderColor: "rgba(52,73,94,0.08)" }}
    data-testid="att-records"
  >
    <div className="flex items-center justify-between px-5 py-4">
      <div>
        <div className="font-display text-lg font-bold text-vision-ink">Recent scans</div>
        <div className="text-xs text-vision-slate/60">
          Most recent first · {records.length} total
        </div>
      </div>
    </div>

    {records.length === 0 ? (
      <div
        className="border-t px-5 py-10 text-center text-sm text-vision-slate/60"
        style={{ borderColor: "rgba(52,73,94,0.08)" }}
        data-testid="att-records-empty"
      >
        No scans yet. Enter an Employee ID above to record the first one.
      </div>
    ) : (
      <>
        <div
          className="hidden grid-cols-[1.3fr_1fr_1fr_90px_90px_80px_110px_120px] gap-4 border-t bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
        >
          <div>Employee</div>
          <div>Department</div>
          <div>Shift</div>
          <div>Scan-In</div>
          <div>Scan-Out</div>
          <div>Late By</div>
          <div>Status</div>
          <div>Scanned by</div>
        </div>
        {records.map((r) => {
          const present = r.status === "present";
          const late = lateByMinutes(r, session);
          const label = attendanceLabel(r);
          return (
            <div
              key={r.id}
              data-testid={`att-record-${r.id}`}
              className="grid grid-cols-1 items-center gap-3 border-t px-5 py-4 md:grid-cols-[1.3fr_1fr_1fr_90px_90px_80px_110px_120px] md:gap-4"
              style={{ borderColor: "rgba(52,73,94,0.08)" }}
            >
              <div className="min-w-0">
                <div className="truncate font-display text-sm font-bold text-vision-ink">
                  {r.name}
                </div>
                <div className="text-xs text-vision-slate/60">{r.employee_id}</div>
              </div>
              <div className="text-sm text-vision-slate">{r.department_name}</div>
              <div className="text-sm text-vision-slate">{r.shift_name}</div>
              <div className="font-display text-sm font-bold text-vision-ink numeric-tabular">
                <Clock className="mr-1 inline h-3.5 w-3.5 text-vision-slate/60" />
                {r.scan_in_time}
              </div>
              <div
                className="font-display text-sm font-bold numeric-tabular"
                data-testid={`att-record-out-${r.id}`}
                style={{ color: r.out_time ? "var(--vision-navy)" : "rgba(52,73,94,0.35)" }}
              >
                {r.out_time ? (
                  <>
                    <LogOut className="mr-1 inline h-3.5 w-3.5" />
                    {r.out_time}
                  </>
                ) : (
                  <span>—</span>
                )}
              </div>
              <div
                className="text-xs font-semibold numeric-tabular"
                data-testid={`att-record-late-${r.id}`}
                style={{ color: late != null ? "var(--vision-error)" : "rgba(52,73,94,0.35)" }}
              >
                {late != null ? `Late by ${late} min` : "—"}
              </div>
              <div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className="chip"
                    style={{
                      backgroundColor: r.out_ts
                        ? "rgba(0, 53, 158, 0.10)"
                        : "rgba(39, 174, 96, 0.14)",
                      color: r.out_ts ? "var(--vision-navy)" : "#1e8a4a",
                      border: r.out_ts
                        ? "1px solid rgba(0, 53, 158, 0.25)"
                        : "1px solid rgba(39, 174, 96, 0.35)",
                    }}
                  >
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full"
                      style={{
                        backgroundColor: r.out_ts
                          ? "var(--vision-navy)"
                          : "var(--vision-success)",
                      }}
                    />
                    {label}
                  </span>
                  {r.is_late && (
                    <span
                      className="chip text-[11px] font-semibold"
                      data-testid={`att-record-late-chip-${r.id}`}
                      style={{
                        backgroundColor: "rgba(235, 87, 87, 0.12)",
                        color: "var(--vision-error)",
                        border: "1px solid rgba(235, 87, 87, 0.35)",
                      }}
                    >
                      Late
                    </span>
                  )}
                </div>
              </div>
              <div
                className="text-xs text-vision-slate"
                data-testid={`att-record-scanned-by-${r.id}`}
              >
                {r.scanned_in_by || r.scanned_out_by ? (
                  <div className="space-y-0.5">
                    {r.scanned_in_by && (
                      <div>
                        <span className="text-vision-slate/60 text-[10px] uppercase font-semibold">In: </span>
                        <span>{formatUserAudit(r.scanned_in_by)}</span>
                      </div>
                    )}
                    {r.scanned_out_by && (
                      <div>
                        <span className="text-vision-slate/60 text-[10px] uppercase font-semibold">Out: </span>
                        <span>{formatUserAudit(r.scanned_out_by)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <span>—</span>
                )}
              </div>
            </div>
          );
        })}
      </>
    )}
  </div>
);

export default AttendancePage;
